import React from 'react';
import {
  cloneDeep,
  filter,
  forEach,
  get,
  groupBy,
  has,
  isEmpty,
  isEqual,
  map,
  mapValues,
  omit,
} from 'lodash';
import {
  Accordion as MuiAccordion,
  AccordionDetails,
  AccordionSummary as MuiAccordionSummary,
  CircularProgress,
} from '@mui/material';
import classnames from 'classnames';
import DestinationPayloadContainer from './DestinationPayloadContainer';
import RestConfigurationSection from './RestConfigurationSection';
import AuthenticateDataSource from '../../data_sources/add_ds_form/AuthenticateDataSource';
import FrequencyField from '../../elements/FrequencyField';
import { getCurrentSelectedOption } from '../../elements/rselect/ReactSelectUtils';
import UserConfirmation from '../../route_utils/UserConfirmation';
import UIUtils from '../../../lib/UIUtils';
import TransformsUtils from '../../../lib/TransformsUtils';
import { validateDataPath } from 'js/lib/ValidationUtils';
import {
  CONNECTION_CATEGORY_REST,
  CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT,
} from 'js/constants/connectionCategories';
import {
  SUPPORT_KEY_SINK_FORM_COMMON,
  SUPPORT_KEY_SINK_FORM_REST_TEMP,
} from 'js/constants/supportPageKeys';
import Constants from '../../../Constants';
import JSONUtils from '../../../lib/JSONUtils';
import './ConfigureTemplatizedAPISink.scss';
import { convertJSONtoScala } from 'js/lib/Scala';
import { FieldBuilderRedesigned } from 'new/elements/FieldBuilderRedesigned/FieldBuilder';
import { FormConfigurationStep } from 'new/components/FormConfigurationStep/FormConfigurationStep';
import HeaderMetaConfiguration from 'js/components/destination/HeaderMetaConfiguration/HeaderMetaConfiguration';
import { styled } from '@mui/system';
import ArrowIcon from '@mui/icons-material/ArrowForwardIosSharp';
import styles from './ConfigureTemplatizedAPISink.module.scss';
import { TEMPLATE_API_TEST_ENABLED, BATCH_API_TEST_ENABLED } from 'js/constants/config';
import { ResourcePopoverMenu } from 'src/new/elements/ResourcePopoverMenu';
import { tryCatchAsync } from 'new/utils/TryCatch';
import { UiMode } from 'new/constants/uiMode';

const CHECK_BOX_LABEL_STYLE = {
  fontSize: 12,
  fontWeight: 500,
  color: '#1F2F40',
  lineHeight: '24px',
  textTransform: 'initial',
  letterSpacing: '.5px',
};

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square children={null} {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': { borderBottom: 0 },
  '&::before': { display: 'none' },
}));

const Icon = <ArrowIcon sx={{ fontSize: '1rem' }} strick="red" />;
const AccordionSummary = styled((props) => <MuiAccordionSummary expandIcon={Icon} {...props} />)(
  () => {
    return {
      padding: 0,
      paddingLeft: 8,
      border: 'none',
      borderRadius: 11,
      backgroundColor: 'var(--color-primary-50)',
      flexDirection: 'row-reverse',
      '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
      },
      '& .MuiAccordionSummary-content': {
        marginLeft: 12,
      },
    };
  },
);

class ConfigureTemplatizedAPISink extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedTemplate: null,
      dataPathValidationResultByField: {},
      requestedMode: null,
    };

    this._getSimpleModeContent = this._getSimpleModeContent.bind(this);
    this._getVisibleFields = this._getVisibleFields.bind(this);
    this._getAdvancedModeContent = this._getAdvancedModeContent.bind(this);
    this._getTextField = this._getTextField.bind(this);
    this._getSelectField = this._getSelectField.bind(this);
    this._handleModeChanged = this._handleModeChanged.bind(this);
    this._handleModeChangeConfirmed = this._handleModeChangeConfirmed.bind(this);
    this._handleCronChanged = this._handleCronChanged.bind(this);
    this._refreshPayload = this._refreshPayload.bind(this);
    this._getResourceDataFields = this._getResourceDataFields.bind(this);
    this._getResourceDataFieldKey = this._getResourceDataFieldKey.bind(this);
    this._loadResourceDataFieldOptions = this._loadResourceDataFieldOptions.bind(this);
  }

  componentDidMount() {
    if (!isEmpty(this.props.samples)) {
      this._refreshPayload();
    }

    this._loadResourceDataFieldOptions();

    if (this.props.setContextualSupport) {
      const {
        paging: { metaInfoById },
        selectedConnection,
      } = this.props;

      const meta = metaInfoById && metaInfoById[selectedConnection];
      const isScript = meta.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT;

      if (isScript) {
        this.props.setContextualSupport(SUPPORT_KEY_SINK_FORM_COMMON);
      } else {
        this.props.setContextualSupport(SUPPORT_KEY_SINK_FORM_REST_TEMP);
      }
    }
  }

  componentWillUnmount() {
    if (this.props.unsetContextualSupport) {
      const {
        paging: { metaInfoById },
        selectedConnection,
      } = this.props;

      const meta = metaInfoById && metaInfoById[selectedConnection];
      const isScript = meta.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT;

      if (isScript) {
        this.props.unsetContextualSupport(SUPPORT_KEY_SINK_FORM_COMMON);
      } else {
        this.props.unsetContextualSupport(SUPPORT_KEY_SINK_FORM_REST_TEMP);
      }
    }
  }

  componentDidUpdate(prevProps) {
    const {
      paging: { sinkConfigById, metaInfoById },
      vendorEndpoints,
      selectedConnection,
      templatizedAPIConfig,
      probeSample,
      samples,
    } = this.props;
    const { connectionTypeCategory } = metaInfoById[selectedConnection];
    const selectedEndpointId = templatizedAPIConfig.vendor_endpoint_id;

    if (connectionTypeCategory === CONNECTION_CATEGORY_REST) {
      // if call response changed, validate data path
      if (probeSample && !isEqual(probeSample, prevProps.probeSample)) {
        const dataPathValidationResultByField = {
          ...this.state.dataPathValidationResultByField,
        };
        const endpointIds = { 0: selectedEndpointId };

        forEach(this._getResourceDataFields(), (field) => {
          endpointIds[this._getResourceDataFieldKey(field)] = field.vendor_endpoint_id;
        });

        forEach(endpointIds, (endpointId, key) => {
          if (
            get(probeSample[key], 'response.output.response') &&
            !isEqual(probeSample[key], get(prevProps.probeSample, key))
          ) {
            const endpoint = vendorEndpoints[endpointId];
            const iterations = get(endpoint, 'source_template["rest.iterations"]', []);
            const stageAPIConfig = iterations.length > 0 ? iterations[iterations.length - 1] : {};
            const dataPathFieldName = 'response.data.path';
            const dataPaths = {};

            if (stageAPIConfig[dataPathFieldName]) {
              dataPaths[dataPathFieldName] = stageAPIConfig[dataPathFieldName];
            }

            const {
              response: {
                output: { response, contentType, statusCode },
              },
            } = probeSample[key];
            const dataPathValidationResult = validateDataPath(
              dataPaths,
              {
                apiResponseContent: response,
                apiResponseContentType: contentType,
                apiResponseCode: statusCode,
              },
              stageAPIConfig,
            );

            dataPathValidationResultByField[key] = {
              ...dataPathValidationResultByField[key],
              ...dataPathValidationResult,
            };
          }
        });

        this.setState({ dataPathValidationResultByField });
      }

      // if the newly selected endpoint has some vendor_resource_data params,
      // get data for them
      if (
        (selectedEndpointId &&
          selectedEndpointId !== prevProps.templatizedAPIConfig.vendor_endpoint_id) ||
        sinkConfigById !== prevProps.paging.sinkConfigById
      ) {
        this._loadResourceDataFieldOptions();
      }
    }

    if (isEmpty(prevProps.samples) && !isEmpty(samples)) {
      this._refreshPayload();
    }
  }

  onChange = ({ name, value }) => {
    const { sinkConfig, onSinkConfigChanged } = this.props;
    onSinkConfigChanged({
      ...sinkConfig,
      [name]: value,
    });
  };

  render() {
    const {
      paging: { metaInfoById },
      vendorEndpoints,
      selectedConnection,
      templatizedAPIConfig,
      onToggleDestinationDraft,
    } = this.props;
    const { requestedMode } = this.state;
    const selectedEndpointId = templatizedAPIConfig?.vendor_endpoint_id;
    const selectedEndpoint = selectedEndpointId && vendorEndpoints?.[selectedEndpointId];

    if (!selectedEndpoint) {
      if (selectedEndpointId != null) {
        return (
          <div className={styles.endpointLoading}>
            <CircularProgress size={32} />
            <p className={styles.endpointLoadingLabel}>Loading API endpoint definition…</p>
          </div>
        );
      }
      console.error('Selected endpoint is missing');
      return null;
    }

    const meta = metaInfoById && metaInfoById[selectedConnection];
    const isScript = meta.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT;
    const isAdvancedMode = !!templatizedAPIConfig?.vendor_id;
    const batchModeTemplate = !!selectedEndpoint?.sink_template?.['batch.mode'];
    const requestPayloadDescription = isScript
      ? 'Samples of data which will be sent to this destination. You can change it by updating the output attributes of this Nexset.'
      : batchModeTemplate === true
        ? "This endpoint has been configured to group multiple samples into one API call to meet the API vendor's requirements. The samples below are a representation of samples used to generate the batched request, and not the actual API request payload itself."
        : 'Payloads for some sample requests that will be made to the selected API endpoint. You can change the payload by updating the output attributes of this Nexset.';
    const payloadPanelHeader = batchModeTemplate === true ? 'Sample for Payload' : 'Sample Payload';
    const { dataSet, sinkConfig, addedDataSink } = this.props;
    const destName = addedDataSink?.name || sinkConfig?.name;
    const destDesc = addedDataSink?.description || sinkConfig?.description;
    const isBatchMode = !!sinkConfig['batch.mode'];
    const isTestingSamplesDisabledForTemplateTab =
      (!isAdvancedMode && !TEMPLATE_API_TEST_ENABLED) || (isBatchMode && !BATCH_API_TEST_ENABLED);
    const resourceConfig = this.getResourceConfig();

    // only show the simple mode for script destinations
    if (isScript) {
      const { dataSet } = this.props;

      return (
        <FormConfigurationStep
          enableCategoryHeader={false}
          headerComponent={
            <HeaderMetaConfiguration
              nexsetId={dataSet?.id}
              isDestination
              name={destName}
              description={destDesc}
              connection={selectedConnection}
              onChange={this.onChange}
              nexsetChipChildren={
                <ResourcePopoverMenu
                  isDestination
                  nexsetId={dataSet?.id}
                  connectorType={selectedConnection}
                  disabledActions={['view-json-config']}
                />
              }
            />
          }
        >
          <div className={styles.container}>
            <div className={styles.left_column}>
              <div className={styles.configuration_holder}>{this._getSimpleModeContent()}</div>
            </div>
            <div className={styles.right_column}>
              <DestinationPayloadContainer
                heading="Sample Request Payloads"
                description={requestPayloadDescription}
                panelHeading={payloadPanelHeader}
                onToggleDestinationDraft={onToggleDestinationDraft}
                addTemplateConfigForTestingSamples={isAdvancedMode}
                disableTabs
                disableTestingTab={isTestingSamplesDisabledForTemplateTab}
                parentDataSet={this.props.parentDataSet}
                parentDataSetSamples={this.props.parentDataSetSamples}
              />
            </div>
          </div>
        </FormConfigurationStep>
      );
    }

    return (
      <div className="configure-templatized-api-container configure-templatized-api-sink-container">
        <HeaderMetaConfiguration
          isDestination
          name={destName}
          nexsetId={dataSet.id}
          description={destDesc}
          connection={selectedConnection}
          onChange={this.onChange}
          nexsetChipChildren={
            <ResourcePopoverMenu
              isDestination
              isTemplatized
              nexsetId={this.props.dataSetId}
              resourceConfig={resourceConfig}
              connectorType={selectedConnection}
              jsonConfigOnSave={this.onSaveAdvancedMode}
              jsonConfigOnClose={this.props?.goBack}
              onSwitchUiMode={this.onSwitchUiMode}
              disabledActions={['view-json-config']}
              resourcePersisted={!!addedDataSink?.id}
            />
          }
          sourceOrDestinationChipChildren={
            <ResourcePopoverMenu
              isTemplatized
              isDestination
              resourceConfig={resourceConfig}
              connectorType={selectedConnection}
              jsonConfigOnSave={this.onSaveAdvancedMode}
              jsonConfigOnClose={this.props?.goBack}
              onSwitchUiMode={this.onSwitchUiMode}
              disabledActions={
                isAdvancedMode
                  ? ['view-nexset-details']
                  : ['view-nexset-details', 'view-json-config']
              }
              resourcePersisted={!!addedDataSink?.id}
            />
          }
        />

        <div className="form-section-tabs-container">
          <div
            data-mode="simple"
            className={classnames('form-section-tab', {
              selected: !isAdvancedMode,
            })}
            onClick={this._handleModeChanged}
          >
            <h1>Template</h1>
          </div>
          <div
            data-mode="advanced"
            className={classnames('form-section-tab', {
              selected: isAdvancedMode,
            })}
            onClick={this._handleModeChanged}
          >
            <h1>Advanced</h1>
          </div>
        </div>
        <div className="form-section-tab-content">
          <div className="configure-templatized-api-sink configure-template rest-configure">
            <div className="rest-configure-left-column">
              <section className="configuration-holder">
                {isAdvancedMode ? this._getAdvancedModeContent() : this._getSimpleModeContent()}
              </section>
            </div>
            <div className="rest-configure-right-column">
              <DestinationPayloadContainer
                heading="Sample Request Payloads"
                description={requestPayloadDescription}
                panelHeading={payloadPanelHeader}
                onToggleDestinationDraft={onToggleDestinationDraft}
                addTemplateConfigForTestingSamples={!isAdvancedMode}
                disableTestingTab={isTestingSamplesDisabledForTemplateTab}
                parentDataSet={this.props.parentDataSet}
                parentDataSetSamples={this.props.parentDataSetSamples}
              />
            </div>
          </div>
        </div>
        {requestedMode && (
          <UserConfirmation
            message="This will cause you to lose all unsaved changes in this tab. Do you want to continue?"
            cancelButtonLabel="Cancel"
            okButtonLabel="Continue"
            onCancel={() => this._handleModeChangeConfirmed(false)}
            onConfirm={() => this._handleModeChangeConfirmed(true)}
          />
        )}
      </div>
    );
  }

  onSaveAdvancedMode = async (sinkConfigString) => {
    const { onSaveAdvancedMode, nextFormStage, onError, sinkConfig } = this.props;

    if (!JSONUtils.isJSON(sinkConfigString)) {
      onError('The provided JSON is invalid. Please check the syntax and try again.');
      return;
    }

    const parsedConfig = JSON.parse(sinkConfigString);
    const { name, description, sink_config, template_config } = parsedConfig;
    const configuration = sink_config ?? template_config;

    const data = {
      ...configuration,
      name,
      description,
      'ui.mode': UiMode.CODE,
    };

    const { error } = await tryCatchAsync(onSaveAdvancedMode(data));
    if (error) {
      onError(error.message);
      return;
    }

    nextFormStage();
  };

  onSwitchUiMode = () => {
    const { sinkConfig, onSinkConfigChanged } = this.props;
    if (typeof onSinkConfigChanged !== 'function') return;

    onSinkConfigChanged({ ...sinkConfig, 'ui.mode': UiMode.VIEW });
  };

  getResourceConfig = () => {
    const { addedDataSink } = this.props;
    const clonedSinkConfig = structuredClone(this.props.sinkConfig);
    const name = addedDataSink?.name || clonedSinkConfig?.name || '';
    const description = addedDataSink?.description || clonedSinkConfig?.description || '';

    delete clonedSinkConfig.name;
    delete clonedSinkConfig.description;

    return {
      name,
      description,
      sink_config: clonedSinkConfig,
    };
  };

  _getVisibleFields() {
    const {
      paging: { sinkConfigById },
      selectedConnection,
      templatizedAPIConfig,
    } = this.props;
    const sinkConfigFields = sinkConfigById[selectedConnection];
    const fieldsByIsEndpoint = groupBy(
      sinkConfigFields,
      (field) => field.name === 'vendor_endpoint_id',
    );
    const otherFields = fieldsByIsEndpoint[false];
    return filter(
      otherFields,
      (field) =>
        UIUtils.shouldRenderField(field, templatizedAPIConfig) &&
        UIUtils.shouldHideField(field, templatizedAPIConfig) === false,
    );
  }

  _getSimpleModeContent() {
    const {
      paging: { sinkConfigById },
      vendorEndpoints,
      selectedConnection,
      templatizedAPIConfig,
    } = this.props;

    const { dataPathValidationResultByField } = this.state;
    const sinkConfigFields = sinkConfigById[selectedConnection];
    const fieldsByIsEndpoint = groupBy(
      sinkConfigFields,
      (field) => field.name === 'vendor_endpoint_id',
    );
    const selectedEndpointId = templatizedAPIConfig?.vendor_endpoint_id;
    const selectedEndpoint = selectedEndpointId && vendorEndpoints[selectedEndpointId];
    const endpointField = (fieldsByIsEndpoint[true] && fieldsByIsEndpoint[true][0]) || {};
    const visibleFields = this._getVisibleFields();
    const isEndpointSelectionDisabled = !(
      endpointField.options &&
      endpointField.options.length === 1 &&
      selectedEndpoint &&
      selectedEndpointId === endpointField.options[0].value
    );

    return (
      <div>
        {this._renderVendorDescription()}

        <div className="input-row">
          <div className="input-holder">
            {isEndpointSelectionDisabled
              ? this._getSelectField({
                  name: 'vendor_endpoint_id',
                  display_name: 'Endpoint',
                  inputType: 'select',
                  unsorted: true,
                  options: endpointField.options,
                })
              : selectedEndpoint && (
                  <p className={styles.input_description}>{selectedEndpoint.display_name}</p>
                )}
          </div>
        </div>

        {selectedEndpoint &&
          (selectedEndpoint.description ||
            (has(selectedEndpoint, 'config.info_url') && selectedEndpoint.config.info_url)) && (
            <div className="data-path-suggestions">
              <h4>Details</h4>
              <div className="suggestions-container endpoint-info-container">
                {selectedEndpoint.description && (
                  <div>
                    {map(selectedEndpoint.description.split(/\n+/), (text, idx) => (
                      <p key={idx}>{text}</p>
                    ))}
                  </div>
                )}
                {has(selectedEndpoint, 'config.info_url') && selectedEndpoint.config.info_url && (
                  <a
                    className="endpoint-doc-link undecorated-link"
                    href={selectedEndpoint.config.info_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open Documentation
                  </a>
                )}
              </div>
            </div>
          )}

        {!isEmpty(visibleFields) &&
          map(visibleFields, (field) => {
            const { name, type, options } = field;
            let fieldComponent;

            if (!isEmpty(options)) {
              fieldComponent = <div className="input-holder">{this._getSelectField(field)}</div>;
            } else if (type === 'frequency') {
              fieldComponent = (
                <FrequencyField
                  name={name}
                  label="Call API"
                  value={templatizedAPIConfig[name]}
                  labelStyle={CHECK_BOX_LABEL_STYLE}
                  iconColor="#4AD496"
                  onChange={this._handleCronChanged}
                />
              );
            } else if (type === 'script_credential') {
              fieldComponent = this._getCredentialField(field);
            } else if (type === 'vendor_resource_data') {
              const fieldKey = this._getResourceDataFieldKey(field);
              const data = get(
                dataPathValidationResultByField,
                `${fieldKey}['response.data.path'].matches`,
              );
              const generatedOptions = map(
                filter(data, (item) => has(item, field.option_property_mapping.value)),
                (item) =>
                  mapValues(field.option_property_mapping, (propertyPath) =>
                    get(item, propertyPath, get(item, field.option_property_mapping.value)),
                  ),
              );

              const customSelectField = {
                ...field,
                options: generatedOptions,
              };

              fieldComponent = (
                <div className="input-holder">{this._getSelectField(customSelectField)}</div>
              );
            } else {
              fieldComponent = <div className="input-holder">{this._getTextField(field)}</div>;
            }

            return (
              <div key={name} className="input-row">
                {fieldComponent}
              </div>
            );
          })}
      </div>
    );
  }

  _renderVendorDescription = () => {
    const {
      paging: { metaInfoById },
      vendors,
      selectedConnection,
    } = this.props;
    const meta = metaInfoById && metaInfoById[selectedConnection];
    const connectionNameLabel = meta ? meta.displayName : 'API';
    const isScript = meta.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT;
    const vendorDescription =
      isScript && vendors[meta.vendorId] && vendors[meta.vendorId].description;

    return (
      <div className={styles.description}>
        <h2 className={styles.label}>Description</h2>
        {vendorDescription ? (
          <p className={styles.description_txt}>{vendorDescription}</p>
        ) : (
          <p className={styles.description_txt}>
            {vendorDescription}
            Select and configure the API endpoint you wish to write data to. Don't see an option for
            the endpoint you wish to use? No problem! Switch to the advanced mode to write to any
            valid {`${connectionNameLabel}`} URL.
          </p>
        )}
      </div>
    );
  };

  _getAdvancedModeContent() {
    const {
      paging,
      currentOrg,
      selectedConnection,
      addedDataSink,
      dataSchema,
      templatizedAPIConfig,
      setContextualSupport,
      unsetContextualSupport,
      onSinkMappingUpdated,
      onSinkConfigChanged,
    } = this.props;

    return (
      <RestConfigurationSection
        paging={paging}
        currentOrg={currentOrg}
        selectedConnection={selectedConnection}
        addedDataSink={addedDataSink}
        dataSchema={dataSchema}
        sinkConfig={templatizedAPIConfig}
        setContextualSupport={setContextualSupport}
        unsetContextualSupport={unsetContextualSupport}
        onSinkMappingUpdated={onSinkMappingUpdated}
        onSinkConfigChanged={onSinkConfigChanged}
      />
    );
  }

  _getTextField(field) {
    const { name } = field;
    const { templatizedAPIConfig, onTemplatizedAPIConfigChanged } = this.props;
    const currentValue = get(templatizedAPIConfig, name, '');
    return (
      <FieldBuilderRedesigned
        id={name}
        field={field}
        currentFieldValue={currentValue}
        isRequired={field.required}
        fieldLabelClassName={styles.label}
        onChange={(name, newValue) => {
          onTemplatizedAPIConfigChanged(name, newValue);
        }}
      />
    );
  }

  _getSelectField(field) {
    const { name, options = [] } = field;
    const { templatizedAPIConfig, onTemplatizedAPIConfigChanged, vendorEndpoints } = this.props;
    const rawValue = templatizedAPIConfig[name];
    let currentValue = getCurrentSelectedOption(options, rawValue, null, null);
    let selectOptions = options;
    if (
      name === 'vendor_endpoint_id' &&
      rawValue != null &&
      currentValue == null &&
      vendorEndpoints &&
      vendorEndpoints[rawValue]
    ) {
      const ep = vendorEndpoints[rawValue];
      currentValue = {
        label: ep.name || String(rawValue),
        value: rawValue,
      };
      selectOptions = Array.isArray(options) ? [currentValue, ...options] : options;
    }
    const fieldForSelect = selectOptions !== options ? { ...field, options: selectOptions } : field;

    return (
      <FieldBuilderRedesigned
        field={fieldForSelect}
        currentFieldValue={currentValue}
        isRequired={field.required}
        id={name}
        fieldLabelClassName={styles.label}
        onChange={(fieldName, currentValue) => {
          onTemplatizedAPIConfigChanged(fieldName, currentValue);
        }}
      />
    );
  }

  onChangeTemplate = (id) => {
    this.setState({ selectedTemplate: Number(id) });
  };

  _getCredentialField(field) {
    const {
      paging: { credentialsAllowed, metaInfoById, connAuthById, connAuthMeta },
      vendors,
      credentials,
      credentialsByType,
      credentialsById,
      credentialsData,
      templatizedAPIConfig,
      isLoadingCredentials,
      onScriptCredentialInputChanged,
      onValidateNewScriptCredential,
      onLoadCredentials,
      onNewDropboxCredential,
      onNewBoxCredential,
    } = this.props;
    const { name, description, display_name, credential_type } = field;
    const { templates, items } = credential_type ? connAuthById[credential_type] : null;
    const connectionAuthMeta = credential_type ? connAuthMeta[credential_type] : null;
    const credentialData = credentialsData[name] || {};
    const selectedCredentialId = templatizedAPIConfig[name];
    const selectedCredential = selectedCredentialId && credentials[selectedCredentialId];

    return (
      <div className="accordion-group">
        <Accordion square defaultExpanded className="accordion-item credential-panel">
          <AccordionSummary className="accordion-title">
            <div className="accordion-title-content">
              <h2>{display_name}</h2>
              <span className="accordion-content-growiteration-info">
                {selectedCredential?.name}
              </span>
            </div>
          </AccordionSummary>

          <AccordionDetails className="accordion-body">
            <div className="accordion-body-content">
              <div>
                {description && <p className="credential-field-description">{description}</p>}
                <AuthenticateDataSource
                  onChangeApiType={this.props.onChangeApiType}
                  formData={credentialData}
                  connectionTypes={credentialsAllowed}
                  connectionMetas={metaInfoById}
                  vendors={vendors}
                  selectedConnection={credential_type}
                  templates={templates}
                  selectedTemplate={this.state.selectedTemplate}
                  onChangeTemplate={this.onChangeTemplate}
                  connectionAuthFields={items}
                  connectionAuthMeta={connectionAuthMeta}
                  dataCredentials={credentialsByType}
                  credentialsById={credentialsById}
                  isFormAwaitingValidation={false}
                  isLoadingData={isLoadingCredentials}
                  onFormInputChanged={(...args) => onScriptCredentialInputChanged(name, ...args)}
                  onCredSelectionChanged={() => {}}
                  onRefreshClicked={onLoadCredentials}
                  onNewDropboxCredential={(...args) => onNewDropboxCredential(name, ...args)}
                  onNewBoxCredential={(...args) => onNewBoxCredential(name, ...args)}
                  nextFormStage={(...args) => onValidateNewScriptCredential(name, ...args)}
                />
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    );
  }

  _handleModeChanged(event) {
    const {
      templatizedAPIConfig,
      vendorEndpoints,
      selectedCredential,
      sinkConfigById,
      selectedConnection,
    } = this.props;

    const { mode } = event.currentTarget.dataset;
    const { vendor_endpoint_id: endpointId } = templatizedAPIConfig;
    let newConfig = {
      ...templatizedAPIConfig,
    };

    if (
      mode === 'advanced' &&
      !!endpointId &&
      vendorEndpoints[endpointId] &&
      !isEmpty(vendorEndpoints[endpointId].sink_template)
    ) {
      // Macro replacement regex
      const placeholderPattern = /\$\{([\w.-]+?)\}/g;

      // update sinkConfig as per advance node
      const newSinkConfigFromTemplate = cloneDeep(vendorEndpoints[endpointId].sink_template);

      // Check and replace macros inside body.transform.function
      if (has(newSinkConfigFromTemplate, 'body.transform.function')) {
        newSinkConfigFromTemplate['body.transform.function'] = UIUtils.replaceMacros(
          {
            templateURL: newSinkConfigFromTemplate['body.transform.function'],
            config: templatizedAPIConfig,
            fields: sinkConfigById[selectedConnection],
            placeholderPattern,
            selectedCredential,
          },
          (value, _key) => {
            let newVal = value;

            let valIsNumber =
              newVal.match(/^[0-9]+$/) != null ||
              newVal
                .split(',')
                .join(1)
                .match(/^[0-9]+$/) != null ||
              false;

            // if number return directly like 1111, or 1,2,3,4
            if (valIsNumber) return newVal;

            let isJSON = JSONUtils.isJSON(newVal);
            if (isJSON) {
              // parse the value
              let JSONVal = JSONUtils.parseJson(value);
              // convert this JSON to scala
              newVal = convertJSONtoScala(JSONVal, null, false);
            } else {
              // Template already wraps placeholders in quotes, return raw value
              newVal = String(newVal);
            }
            return newVal;
          },
        );
      }

      // Updating template sink confis in the sinkConfig
      newConfig = {
        ...newSinkConfigFromTemplate,
        ...omit(newConfig, ['sink_type']),
        ...(has(newSinkConfigFromTemplate, 'batch.mode')
          ? { 'ui.batch_algo': Constants.GROUPING_ALGO[2].value }
          : {}),
      };

      // replace placeholders inside source_template values
      let url_template = newSinkConfigFromTemplate['url.template'];
      let updatedValue = UIUtils.replaceMacros({
        templateURL: url_template,
        config: templatizedAPIConfig,
        fields: sinkConfigById[selectedConnection],
        placeholderPattern,
        selectedCredential,
      });

      if (
        placeholderPattern.test(updatedValue) &&
        selectedCredential &&
        has(selectedCredential.credentials_non_secure_data, 'test.url')
      ) {
        //if macros could not be replaced, fall back to using credential test url
        updatedValue = selectedCredential.credentials_non_secure_data['test.url'];
      }

      newConfig = {
        ...newConfig,
        advanced_templatized_api: true,
        'url.template': updatedValue,
      };
    } else if (mode !== 'advanced') {
      // When changing mode to template, only keeping fields and vendor_endpoint_id value
      const visibleFields = this._getVisibleFields();
      newConfig = {
        vendor_endpoint_id: endpointId,
        advanced_templatized_api: false,
      };
      forEach(visibleFields, (field) => {
        if (templatizedAPIConfig[field.name]) {
          newConfig[field.name] = templatizedAPIConfig[field.name];
        }
      });
    }
    this._changeMode(mode, newConfig);
  }

  _handleModeChangeConfirmed(isConfirmed) {
    if (isConfirmed) {
      const { requestedMode } = this.state;
      this._changeMode(requestedMode);
    }

    this.setState({ requestedMode: null });
  }

  _handleCronChanged(value, fieldName) {
    this.props.onTemplatizedAPIConfigChanged(fieldName, value);
  }

  _changeMode(mode, newConfig = {}) {
    const { onTemplatizedAPIModeChanged } = this.props;
    const isAdvancedMode = mode === 'advanced';

    onTemplatizedAPIModeChanged(isAdvancedMode, newConfig);
  }

  _refreshPayload() {
    let { dataSetId, samples } = this.props;
    let txObject = TransformsUtils.getTransformSpecForFlatToNested();

    this.props.fetchTransformedPayloadFromEditorForRest(txObject, samples, dataSetId);
  }

  _getResourceDataFields() {
    const {
      paging: { sinkConfigById },
      selectedConnection,
      templatizedAPIConfig,
    } = this.props;
    const sinkConfigFields = sinkConfigById[selectedConnection];

    return filter(
      sinkConfigFields,
      (field) =>
        field?.type === 'vendor_resource_data' &&
        UIUtils.shouldRenderField(field, templatizedAPIConfig) &&
        UIUtils.shouldHideField(field, templatizedAPIConfig) === false,
    );
  }

  _getResourceDataFieldKey(field) {
    return `vendor_resource_data_${field.param_id}`;
  }

  _loadResourceDataFieldOptions() {
    const {
      paging: { metaInfoById },
      selectedConnection,
      selectedCredentialId,
      templatizedAPIConfig,
      probeSample,
      probeGetSample,
    } = this.props;
    const { connectionTypeCategory } = metaInfoById[selectedConnection];
    const endpointId = templatizedAPIConfig?.vendor_endpoint_id;

    if (endpointId && connectionTypeCategory === CONNECTION_CATEGORY_REST) {
      forEach(this._getResourceDataFields(), (field) => {
        const fieldKey = this._getResourceDataFieldKey(field);

        if (!probeSample || !probeSample[fieldKey]) {
          probeGetSample(selectedCredentialId, fieldKey, {
            vendor_endpoint_id: field.vendor_endpoint_id,
            template_config: field.template_config,
            resource_type: 'SOURCE',
          });
        }
      });
    }
  }
}

export default ConfigureTemplatizedAPISink;
